import { Download, X } from "lucide-react";
import { useEffect } from "react";
import { WashBooking } from "../../types/admin/washBookings.types";
import { WashStatusProgress } from "./WashStatusProgress";
import { useTranslation } from "react-i18next";
import { downloadWashBookingPDF } from "../../utils/downloadWashBookingPDF";

export const WashBookingDetailsOverlay = ({
  booking,
  onClose,
}: {
  booking: WashBooking;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center text-primaryText dark:text-primaryTextDark">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative mt-20 w-full max-w-3xl max-h-[85vh] bg-white dark:bg-gray-900 rounded-xl shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-6">
          <h2 className="text-xl font-semibold">{t("Wash Booking Details")}</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadWashBookingPDF(booking)}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <Download size={20} />
            </button>

            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scroll">
          <WashStatusProgress status={booking.status} />

          <Section title={t("Booking Information")}>
            <Detail label={t("Booking ID")} value={booking._id} />
            <Detail label={t("Status")} value={booking.status} />
            <Detail label={t("Date")} value={booking.date.split("T")[0]} />
            <Detail label={t("Slot Time")} value={booking.slotTime} />
            <Detail label={t("Slot Ref")} value={booking.slotRef} />
            <Detail label={t("Price")} value={`₹${booking.price}`} />
          </Section>

          <Section title={t("Package")}>
            <Detail
              label={t("Title")}
              value={booking.package?.title || booking.packageId}
            />
            {booking.package?.services?.length ? (
              <Detail
                label={t("Services")}
                value={booking.package.services.join(", ")}
              />
            ) : null}
          </Section>

          <Section title={t("Car Details")}>
            <Detail
              label={t("Car")}
              value={
                booking.car
                  ? `${booking.car.brand} ${booking.car.model} (${booking.car.number})`
                  : booking.carId
              }
            />
          </Section>

          <Section title={t("Location")}>
            <Detail label={t("City")} value={booking.city} />
            <Detail label={t("City ID")} value={booking.cityId} />
          </Section>

          <Section title={t("Customer")}>
            <Detail
              label={t("Name")}
              value={booking.user?.name || booking.userId}
            />
            {booking.user?.phone && (
              <Detail label={t("Phone")} value={booking.user.phone} />
            )}
            {booking.user?.email && (
              <Detail label={t("Email")} value={booking.user.email} />
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-3">
    <h3 className="font-semibold">{title}</h3>
    {children}
  </div>
);

const Detail = ({ label, value }: { label: string; value: string }) => (
  <div className="flex gap-2 text-sm">
    <span className="font-medium w-32 shrink-0">{label}:</span>
    <span className="break-all text-gray-700 dark:text-gray-300">{value}</span>
  </div>
);
